import { useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { PipecatClient } from '@pipecat-ai/client-js'
import { SmallWebRTCTransport } from '@pipecat-ai/small-webrtc-transport'
import { Room, RoomEvent } from 'livekit-client'
import { Loader2, Mic, MicOff, Phone, PhoneOff, Video, VideoOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { capitalize } from '@/lib/utils'
import DevicePickers from '@/components/DevicePickers'
import AudioOrb from '@/components/AudioOrb'
import { createLatencyProbe } from '@/lib/latencyProbe'
import doctorImage from '../../assets/images/doctor.webp'

function buildEmptyIntake(schema) {
  return (schema || []).reduce((acc, f) => ({ ...acc, [f.key]: '' }), {})
}

function formatMmSs(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r.toString().padStart(2, '0')}`
}

export default function Triage({ conversationUuid, systemPromptB64, intakeSchema = [], intakeSchemaB64, avatarId, avatarPreviewUrl, voiceGender, voiceMap, defaultModel = 'gemini-live', currentUser, anonCall }) {
  const voiceMapB64 = voiceMap && Object.keys(voiceMap).length > 0 ? btoa(JSON.stringify(voiceMap)) : null
  const emptyIntake = buildEmptyIntake(intakeSchema)
  const intakeKeySet = new Set(intakeSchema.map((f) => f.key))
  const isAnon = !currentUser && anonCall != null
  const anonLimit = anonCall?.limitSeconds ?? 120
  const anonInitialRemaining = isAnon ? Math.max(0, anonCall?.secondsRemaining ?? anonLimit) : null

  const avatarVideoRef = useRef(null)
  const localVideoRef = useRef(null)
  const remoteAudioRef = useRef(null)
  const clientRef = useRef(null)
  const avatarRoomRef = useRef(null)
  const avatarSessionRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const mutedRef = useRef(false)
  const recorderRef = useRef(null)
  const recorderChunksRef = useRef([])
  const recorderContextRef = useRef(null)
  const callStartedRef = useRef(false)
  const persistedRef = useRef(false)
  const latencyRef = useRef(null)

  const [status, setStatus] = useState('idle')
  const [muted, setMuted] = useState(false)
  const [camOff, setCamOff] = useState(false)
  const [error, setError] = useState('')
  const [hasAvatarVideo, setHasAvatarVideo] = useState(false)
  const [hasVideo, setHasVideo] = useState(false)
  const [intake, setIntake] = useState(emptyIntake)
  const [recentlyUpdated, setRecentlyUpdated] = useState({})
  const [completionSummary, setCompletionSummary] = useState(null)
  const [avatarNotice, setAvatarNotice] = useState('')
  const [model, setModel] = useState(defaultModel)
  const [pcClient, setPcClient] = useState(null)
  const [anonSecondsRemaining, setAnonSecondsRemaining] = useState(anonInitialRemaining)
  const [showAnonExhausted, setShowAnonExhausted] = useState(isAnon && anonInitialRemaining <= 0)
  const callStartedAtRef = useRef(null)
  const lastHeartbeatSecondsRef = useRef(0)
  const remainingAtCallStartRef = useRef(anonInitialRemaining ?? 0)

  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

  const attachLocalVideoTrack = (client = clientRef.current) => {
    const videoTrack = client?.tracks()?.local?.video
    const video = localVideoRef.current
    if (!videoTrack || !video) {
      setHasVideo(false)
      return
    }

    const stream = new MediaStream([videoTrack])
    mediaStreamRef.current = stream
    if (video.srcObject !== stream) {
      video.srcObject = stream
    }
    setHasVideo(true)

    const playPromise = video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((playError) => {
        console.warn('Local video play() failed:', playError)
      })
    }

    maybeStartRecording()
  }

  const attachRemoteAudioTrack = (track) => {
    const audio = remoteAudioRef.current
    if (!audio || !track || track.kind !== 'audio') return

    const stream = new MediaStream([track])
    if (audio.srcObject !== stream) {
      audio.srcObject = stream
    }
    audio.muted = false
    audio.volume = 1

    const playPromise = audio.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((playError) => {
        console.warn('Remote audio play() failed:', playError)
      })
    }

    latencyRef.current?.attachBotAudioTrack(track)
    maybeStartRecording()
  }

  const maybeStartRecording = () => {
    if (recorderRef.current) return
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return

    const client = clientRef.current
    const localVideoStream = mediaStreamRef.current
    const localAudioTrack = client?.tracks?.()?.local?.audio
    const remoteAudioStream = remoteAudioRef.current?.srcObject
    if (!localVideoStream || !localAudioTrack) return

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const dest = audioContext.createMediaStreamDestination()

      audioContext.createMediaStreamSource(new MediaStream([localAudioTrack])).connect(dest)
      if (remoteAudioStream && remoteAudioStream.getAudioTracks?.().length > 0) {
        audioContext.createMediaStreamSource(remoteAudioStream).connect(dest)
      }

      const combined = new MediaStream([
        ...localVideoStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ])
      const mimeType = pickRecorderMimeType()
      const recorder = mimeType
        ? new MediaRecorder(combined, { mimeType })
        : new MediaRecorder(combined)
      recorderChunksRef.current = []
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recorderChunksRef.current.push(event.data)
        }
      }
      recorder.start(1000)
      recorderRef.current = recorder
      recorderContextRef.current = audioContext
    } catch (recordError) {
      console.warn('Recording failed to start:', recordError)
    }
  }

  const stopRecording = () =>
    new Promise((resolve) => {
      const recorder = recorderRef.current
      const audioContext = recorderContextRef.current
      const cleanup = () => {
        recorderRef.current = null
        recorderContextRef.current = null
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(() => {})
        }
      }
      if (!recorder || recorder.state === 'inactive') {
        cleanup()
        resolve(null)
        return
      }
      recorder.onstop = () => {
        const chunks = recorderChunksRef.current
        recorderChunksRef.current = []
        cleanup()
        const blob = chunks.length > 0 ? new Blob(chunks, { type: recorder.mimeType || 'video/webm' }) : null
        resolve(blob)
      }
      try {
        recorder.stop()
      } catch (stopError) {
        console.warn('Recorder stop failed:', stopError)
        cleanup()
        resolve(null)
      }
    })

  const uploadRecording = async (patientId, blob) => {
    if (!patientId || !blob) return
    const form = new FormData()
    const extension = (blob.type || 'video/webm').includes('mp4') ? 'mp4' : 'webm'
    form.append('recording', blob, `triage-${patientId}.${extension}`)
    try {
      const response = await fetch(`/patients/${patientId}/recording`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() || '' },
        body: form,
      })
      if (!response.ok) {
        console.warn('Failed to upload recording:', await response.text())
      }
    } catch (uploadError) {
      console.warn('Failed to upload recording:', uploadError)
    }
  }

  const attachAvatarVideoTrack = (track) => {
    const video = avatarVideoRef.current
    const mediaTrack = track?.mediaStreamTrack
    if (!video || !mediaTrack || mediaTrack.kind !== 'video') return

    const stream = new MediaStream([mediaTrack])
    if (video.srcObject !== stream) {
      video.srcObject = stream
    }
    setHasAvatarVideo(true)

    const playPromise = video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((playError) => {
        console.warn('LiveAvatar video play() failed:', playError)
      })
    }

    latencyRef.current?.attachAvatarVideo(video, track)
  }

  const startLiveAvatarSession = async () => {
    const liveAvatarHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken() || '',
    }
    if (avatarId) liveAvatarHeaders['X-Avatar-Id'] = avatarId
    if (voiceGender) liveAvatarHeaders['X-Voice-Gender'] = voiceGender
    if (voiceMapB64) liveAvatarHeaders['X-Voice-Map-B64'] = voiceMapB64
    const sessionRequestedAt = performance.now()
    const response = await fetch('/api/realtime/live_avatar_sessions', {
      method: 'POST',
      headers: liveAvatarHeaders,
      body: JSON.stringify({}),
    })
    const payload = await response.json()
    if (!response.ok) {
      throw new Error(payload?.error || payload?.detail || 'Unable to start the LiveAvatar session.')
    }
    latencyRef.current?.mark(`LiveAvatar session created (${Math.round(performance.now() - sessionRequestedAt)}ms)`)

    const room = new Room({ adaptiveStream: true, dynacast: true })
    room.on(RoomEvent.TrackSubscribed, (track) => {
      latencyRef.current?.mark(`LiveKit ${track?.kind || 'unknown'} track subscribed`)
      attachAvatarVideoTrack(track)
    })
    avatarRoomRef.current = room
    avatarSessionRef.current = payload
    const roomConnectStartedAt = performance.now()
    await room.connect(payload.livekit_url, payload.livekit_client_token)
    latencyRef.current?.mark(`LiveKit room connected (${Math.round(performance.now() - roomConnectStartedAt)}ms)`)
    return payload
  }

  const stopLiveAvatarSession = async () => {
    if (avatarRoomRef.current) {
      avatarRoomRef.current.disconnect()
      avatarRoomRef.current = null
    }
    if (avatarVideoRef.current) {
      avatarVideoRef.current.srcObject = null
    }
    setHasAvatarVideo(false)

    const sessionId = avatarSessionRef.current?.session_id
    avatarSessionRef.current = null
    if (!sessionId) return

    try {
      await fetch(`/api/realtime/live_avatar_sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
      })
    } catch (stopError) {
      console.warn('LiveAvatar session cleanup failed:', stopError)
    }
  }

  const applyIntakeUpdate = (args) => {
    if (!args || typeof args !== 'object') return
    const updates = {}
    for (const [key, value] of Object.entries(args)) {
      if (typeof value === 'string' && intakeKeySet.has(key)) {
        updates[key] = value
      }
    }
    if (Object.keys(updates).length === 0) return

    setIntake((prev) => ({ ...prev, ...updates }))
    setRecentlyUpdated(updates)
    setTimeout(() => {
      setRecentlyUpdated((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(updates)) {
          if (next[key] === updates[key]) delete next[key]
        }
        return next
      })
    }, 2500)
  }

  const intakeRef = useRef(emptyIntake)
  useEffect(() => {
    intakeRef.current = intake
  }, [intake])

  const persistPatient = async () => {
    const payload = {
      conversation_uuid: conversationUuid,
      patient: {
        intake_data: intakeRef.current,
      },
    }

    try {
      const response = await fetch('/patients', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        body: JSON.stringify(payload),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) {
        console.warn('Failed to persist patient:', body)
        return null
      }
      if (body?.estimated_esi_score != null || body?.esi_reason) {
        setCompletionSummary({
          estimated_esi_score: body.estimated_esi_score,
          esi_reason: body.esi_reason,
        })
      }
      return body?.id ?? null
    } catch (saveError) {
      console.warn('Failed to persist patient:', saveError)
      return null
    }
  }

  const handleServerMessage = (message) => {
    if (!message || typeof message !== 'object') return
    if (message.type === 'intake_update') {
      applyIntakeUpdate(message.payload)
    } else if (message.type === 'evaluation_complete') {
      setCompletionSummary({ estimated_esi_score: null, esi_reason: 'Estimating…' })
      setStatus('ended')
      finalizeCall()
    }
  }

  const reportAnonUsage = async ({ keepalive = false } = {}) => {
    if (!isAnon) return null
    const startedAt = callStartedAtRef.current
    if (!startedAt) return null
    const elapsedTotal = Math.floor((Date.now() - startedAt) / 1000)
    const delta = Math.max(0, elapsedTotal - lastHeartbeatSecondsRef.current)
    if (delta === 0) return null
    lastHeartbeatSecondsRef.current = elapsedTotal
    try {
      const response = await fetch('/api/anon_usage', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken() || '',
        },
        body: JSON.stringify({ seconds: delta }),
        keepalive,
      })
      if (!response.ok) return null
      const body = await response.json().catch(() => null)
      if (body && typeof body.seconds_remaining === 'number') {
        setAnonSecondsRemaining(body.seconds_remaining)
        if (body.exhausted) setShowAnonExhausted(true)
      }
      return body
    } catch (e) {
      console.warn('Failed to report anon usage:', e)
      return null
    }
  }

  const finalizeCall = async () => {
    if (persistedRef.current) return
    persistedRef.current = true
    const blob = await stopRecording()
    await reportAnonUsage()
    await cleanupCall()
    const patientId = await persistPatient()
    if (patientId && blob) {
      await uploadRecording(patientId, blob)
    }
  }

  const startCall = async () => {
    if (isAnon && anonSecondsRemaining !== null && anonSecondsRemaining <= 0) {
      setShowAnonExhausted(true)
      return
    }
    setStatus('starting')
    setError('')
    setIntake(emptyIntake)
    setRecentlyUpdated({})
    setCompletionSummary(null)
    setAvatarNotice('')
    callStartedRef.current = true
    persistedRef.current = false
    callStartedAtRef.current = Date.now()
    lastHeartbeatSecondsRef.current = 0
    remainingAtCallStartRef.current = isAnon ? Math.max(0, anonSecondsRemaining ?? 0) : Infinity

    latencyRef.current?.stop()
    latencyRef.current = createLatencyProbe()
    latencyRef.current.mark(`call starting (model=${model})`)

    let liveAvatarSession = null
    try {
      liveAvatarSession = await startLiveAvatarSession()
    } catch (avatarError) {
      console.warn('LiveAvatar unavailable, continuing audio-only:', avatarError)
      setAvatarNotice('Avatar unavailable — continuing in audio-only mode.')
    }

    try {
      const client = new PipecatClient({
        transport: new SmallWebRTCTransport(),
        enableMic: true,
        enableCam: true,
        callbacks: {
          onConnected: () => {
            latencyRef.current?.mark('pipecat WebRTC connected')
            setStatus('connected')
          },
          onDisconnected: () => setStatus((current) => (current === 'connected' ? 'ended' : current)),
          onError: (message) => {
            const errorMessage = message?.data?.error || message?.message || 'Realtime connection error'
            setError(errorMessage)
            setStatus('error')
          },
          onBotReady: () => latencyRef.current?.mark('pipecat bot ready'),
          onUserStartedSpeaking: () => latencyRef.current?.markUserStartedSpeaking(),
          onUserStoppedSpeaking: () => latencyRef.current?.markUserStoppedSpeaking(),
          onBotStartedSpeaking: () => latencyRef.current?.markBotStartedSpeaking(),
          onBotStoppedSpeaking: () => latencyRef.current?.markBotStoppedSpeaking(),
          onServerMessage: handleServerMessage,
          onTrackStarted: (track, participant) => {
            if (track?.kind === 'audio' && !participant?.local) {
              attachRemoteAudioTrack(track)
            } else {
              attachLocalVideoTrack(client)
            }
          },
          onCamUpdated: () => attachLocalVideoTrack(client),
        },
      })
      clientRef.current = client
      setPcClient(client)

      await client.initDevices()
      attachLocalVideoTrack(client)
      const offerHeaders = {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken() || '',
        'X-Triage-Model': model,
      }
      if (liveAvatarSession) {
        offerHeaders['X-Live-Avatar-Session-ID'] = liveAvatarSession.session_id
      }
      if (systemPromptB64) {
        offerHeaders['X-System-Prompt-B64'] = systemPromptB64
      }
      if (voiceGender) {
        offerHeaders['X-Voice-Gender'] = voiceGender
      }
      if (voiceMapB64) {
        offerHeaders['X-Voice-Map-B64'] = voiceMapB64
      }
      if (intakeSchemaB64) {
        offerHeaders['X-Intake-Schema-B64'] = intakeSchemaB64
      }
      if (conversationUuid) {
        offerHeaders['X-Conversation-Uuid'] = conversationUuid
      }
      await client.connect({
        webrtcRequestParams: {
          endpoint: '/api/realtime/offer',
          headers: new Headers(offerHeaders),
        },
      })
      attachLocalVideoTrack(client)
      latencyRef.current?.setPipecatClient(client)
      setStatus('connected')
    } catch (startError) {
      const message = startError?.message || 'Unable to start the triage call.'
      if (isAnon && /trial is up|sign in to keep going|402/i.test(message)) {
        setShowAnonExhausted(true)
      }
      setError(message)
      await cleanupCall()
      setStatus('error')
    }
  }

  // Tick once a second while the call is connected to keep the remaining
  // budget fresh and to disconnect anonymous users when their 2 minutes run
  // out. Heartbeats fire every ~20s so the server-side session total stays
  // accurate even if the user keeps a single long call open.
  useEffect(() => {
    if (!isAnon) return
    if (status !== 'connected') return
    const interval = setInterval(() => {
      const startedAt = callStartedAtRef.current
      if (!startedAt) return
      const elapsed = Math.floor((Date.now() - startedAt) / 1000)
      const remaining = Math.max(0, remainingAtCallStartRef.current - elapsed)
      setAnonSecondsRemaining(remaining)
      if (elapsed - lastHeartbeatSecondsRef.current >= 20) {
        reportAnonUsage()
      }
      if (remaining <= 0) {
        setShowAnonExhausted(true)
        setStatus('ended')
        finalizeCall().catch((err) => console.warn('finalizeCall on trial expiry failed:', err))
      }
    }, 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, isAnon])

  const cleanupCall = async () => {
    latencyRef.current?.stop()
    latencyRef.current = null
    if (recorderRef.current) {
      await stopRecording()
    }
    if (clientRef.current) {
      try { await clientRef.current.disconnect() } catch (_) { /* noop */ }
      clientRef.current = null
      setPcClient(null)
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null
    }
    await stopLiveAvatarSession()
    setHasVideo(false)
  }

  const stopCall = async () => {
    setStatus('ended')
    await finalizeCall()
  }

  const toggleMute = () => {
    setMuted((current) => {
      const next = !current
      mutedRef.current = next
      clientRef.current?.enableMic(!next)
      return next
    })
  }

  const toggleCam = () => {
    setCamOff((current) => {
      const next = !current
      try { clientRef.current?.enableCam(!next) } catch (e) { console.warn('enableCam failed:', e) }
      if (next) setHasVideo(false)
      return next
    })
  }

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAnon) reportAnonUsage({ keepalive: true })
      if (!callStartedRef.current || persistedRef.current) return
      persistedRef.current = true
      const payload = JSON.stringify({
        conversation_uuid: conversationUuid,
        patient: {
          intake_data: intakeRef.current,
        },
      })
      try {
        fetch('/patients', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken() || '',
          },
          body: payload,
          keepalive: true,
        })
      } catch (_) {
        /* best effort */
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (callStartedRef.current && !persistedRef.current) {
        finalizeCall().catch((err) => console.warn('finalizeCall on unmount failed:', err))
      } else {
        cleanupCall()
      }
    }
  }, [])

  useEffect(() => {
    if (hasVideo) attachLocalVideoTrack()
  }, [hasVideo])

  const callActive = status === 'connected' || status === 'starting'
  const canStart = status === 'idle' || status === 'ended' || status === 'error'
  const showStartOverlay = status === 'idle' || status === 'error'

  const returnPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''

  return (
    <div className="min-h-screen bg-background">
      {showAnonExhausted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-2xl">
            <h2 className="text-lg font-semibold tracking-tight">Your 2-minute trial is up</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign in with your work email (or create an account) to keep talking. We use this to track usage and improve the experience.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <a
                href={`/users/sign_in?return_to=${encodeURIComponent(returnPath)}`}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                Sign in
              </a>
              <a
                href={`/users/sign_up?return_to=${encodeURIComponent(returnPath)}`}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-md border px-4 text-sm font-semibold hover:bg-muted"
              >
                Create account
              </a>
            </div>
          </div>
        </div>
      )}
      <audio ref={remoteAudioRef} autoPlay playsInline />
      <header className="border-b bg-background/95">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link href="/" className="text-xl font-bold tracking-tight">
              Live Triage · Mount Sinai
            </Link>
            <span
              className="rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800"
              title="This feature is still in beta"
            >
              Beta
            </span>
            {conversationUuid && (
              <span className="rounded-md border bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground" title={conversationUuid}>
                {conversationUuid.slice(0, 8)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAnon && anonSecondsRemaining !== null && (
              <span
                className={`hidden rounded-md border px-2 py-1 text-xs font-medium sm:inline-flex ${
                  anonSecondsRemaining <= 30
                    ? 'border-rose-300 bg-rose-50 text-rose-700'
                    : 'border-amber-300 bg-amber-50 text-amber-800'
                }`}
                title="Anonymous trial time remaining"
              >
                Trial: {formatMmSs(anonSecondsRemaining)} left
              </span>
            )}
            {!canStart && (
              <Button onClick={stopCall} variant="outline" size="sm">
                <PhoneOff />
                End call
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto grid gap-8 px-4 py-10 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section className="relative aspect-video overflow-hidden rounded-lg border bg-card shadow-sm">
          <img
            src={avatarPreviewUrl || doctorImage}
            onError={(event) => {
              if (event.currentTarget.src.indexOf('/doctor.svg') === -1) {
                event.currentTarget.src = '/doctor.svg'
              }
            }}
            alt="Avatar preview"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity ${
              hasAvatarVideo ? 'opacity-0' : 'opacity-100'
            }`}
          />
          <video
            ref={avatarVideoRef}
            autoPlay
            playsInline
            muted
            className={`absolute inset-0 h-full w-full object-cover transition-opacity ${
              hasAvatarVideo ? 'opacity-100' : 'opacity-0'
            }`}
          />

          <AudioOrb
            audioRef={remoteAudioRef}
            active={status === 'connected' && !hasAvatarVideo && Boolean(avatarNotice)}
          />

          {showStartOverlay && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <Button
                onClick={startCall}
                size="lg"
                className="h-16 gap-3 px-10 text-lg font-semibold shadow-2xl"
              >
                <Phone className="size-5" />
                Start call
              </Button>
            </div>
          )}

          {status === 'ended' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/55 text-white">
              <p className="text-2xl font-semibold tracking-tight">Call ended</p>
              <Button
                onClick={startCall}
                size="lg"
                className="h-12 gap-3 px-7 font-semibold shadow-2xl"
              >
                <Phone className="size-5" />
                Start new call
              </Button>
            </div>
          )}

          {status === 'starting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/55 text-white/90">
              <Loader2 className="size-10 animate-spin" />
              <p className="text-sm font-medium tracking-wide">Connecting…</p>
            </div>
          )}

          {callActive && (
            <div className="absolute bottom-2 right-2 h-24 w-32 overflow-hidden rounded-md border-2 border-white/90 bg-zinc-950 shadow-2xl sm:bottom-4 sm:right-4 sm:h-36 sm:w-52">
              <video
                ref={(node) => {
                  localVideoRef.current = node
                  if (node && mediaStreamRef.current) attachLocalVideoTrack()
                }}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
              />
              {!hasVideo && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-xs text-white/80">
                  Camera off
                </div>
              )}
            </div>
          )}
        </section>

        <aside className="space-y-4">
          <section className="rounded-lg border bg-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold tracking-tight">Triage intake</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  The nurse will fill this in as you talk. Look here to verify spelling.
                </p>
              </div>
              <StatusPill status={status} />
            </div>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={toggleMute}
                  disabled={status !== 'connected'}
                  variant={muted ? 'outline' : 'default'}
                >
                  {muted ? <MicOff /> : <Mic />}
                  {muted ? 'Muted' : 'Live'}
                </Button>
                <Button
                  onClick={toggleCam}
                  disabled={status !== 'connected'}
                  variant={camOff ? 'outline' : 'default'}
                >
                  {camOff ? <VideoOff /> : <Video />}
                  {camOff ? 'Camera off' : 'Camera on'}
                </Button>
              </div>
              <DevicePickers client={pcClient} status={status} />
            </div>
          </section>

          <IntakeForm intake={intake} recentlyUpdated={recentlyUpdated} schema={intakeSchema} />

          {completionSummary && (
            <section className="rounded-lg border bg-card p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold tracking-tight">Estimated ESI</h2>
                <span className="rounded-md border bg-muted px-2 py-1 text-xs font-medium">
                  {completionSummary.estimated_esi_score || 'Pending'}
                </span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                {completionSummary.esi_reason || 'No reason provided.'}
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                The care team will review everything.
              </p>
            </section>
          )}

          {avatarNotice && (
            <section className="rounded-lg border border-amber-500/20 bg-amber-50 p-4 text-sm text-amber-900">
              {avatarNotice}
            </section>
          )}

          {error && (
            <section className="rounded-lg border border-destructive/20 bg-destructive/10 p-5">
              <p className="text-sm text-destructive">{error}</p>
            </section>
          )}
        </aside>
      </main>
    </div>
  )
}

function IntakeForm({ intake, recentlyUpdated, schema }) {
  const sections = []
  let currentSection = null
  for (const field of schema) {
    const sectionName = field.section || 'Intake'
    if (!currentSection || currentSection.name !== sectionName) {
      currentSection = { name: sectionName, fields: [] }
      sections.push(currentSection)
    }
    currentSection.fields.push(field)
  }

  return (
    <section className="rounded-lg border bg-card">
      <div className="border-b px-5 py-3">
        <h2 className="text-sm font-semibold tracking-tight">Patient intake</h2>
      </div>
      <div className="divide-y">
        {sections.map((section) => (
          <div key={section.name} className="px-5 py-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {section.name}
            </p>
            <dl className="space-y-2">
              {section.fields.map((field) => {
                const value = intake[field.key]
                const justUpdated = field.key in recentlyUpdated
                return (
                  <div key={field.key} className="grid grid-cols-[120px_1fr] items-start gap-3">
                    <dt className="pt-0.5 text-xs text-muted-foreground">{field.label}</dt>
                    <dd
                      className={`text-sm transition-colors ${
                        value
                          ? justUpdated
                            ? 'rounded bg-green-100 px-1.5 py-0.5 text-green-900'
                            : 'text-foreground'
                          : 'text-muted-foreground/40'
                      }`}
                    >
                      {capitalize(value) || '—'}
                    </dd>
                  </div>
                )
              })}
            </dl>
          </div>
        ))}
      </div>
    </section>
  )
}

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return null
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ]
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }
  return null
}

function StatusPill({ status }) {
  const labels = {
    idle: 'Idle',
    starting: 'Starting',
    connected: 'Live',
    ended: 'Call ended',
    error: 'Error',
  }

  const className =
    status === 'connected'
      ? 'border-green-600/20 bg-green-600/10 text-green-700'
      : status === 'error'
        ? 'border-destructive/20 bg-destructive/10 text-destructive'
        : 'border-border bg-muted text-muted-foreground'

  return (
    <span className={`inline-flex h-6 items-center rounded-md border px-2 text-xs font-medium ${className}`}>
      {labels[status] || status}
    </span>
  )
}
